import React, { useState, useMemo } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { BookOpen, Sparkles, Star } from 'lucide-react';
import { BookPanel } from './BookReader/BookPanel';
import { SampleContent } from './BookReader/SampleContent';

// Floating magical elements
const FloatingElements = () => {
  const elements = useMemo(() =>
    [...Array(20)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 8 + 4,
      duration: Math.random() * 5 + 5,
      delay: Math.random() * 3
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {elements.map((el) => (
        <motion.div
          key={el.id}
          className="absolute"
          style={{
            left: `${el.x}%`,
            top: `${el.y}%`
          }}
          animate={{
            y: [-15, 15, -15],
            rotate: [0, 360]
          }}
          transition={{
            duration: el.duration,
            delay: el.delay,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <Star
            size={el.size}
            className="text-amber-400/30"
            fill="currentColor"
          />
        </motion.div>
      ))}
    </div>
  );
};

// 3D Book Card Component
const BookCard = ({ book, index, onReadClick }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-100, 100], [15, -15]);
  const rotateY = useTransform(x, [-100, 100], [-15, 15]);

  const springConfig = { stiffness: 300, damping: 30 };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const rotateYSpring = useSpring(rotateY, springConfig);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 80, rotateY: -30 }}
      whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{
        delay: index * 0.1,
        duration: 0.8,
        type: 'spring',
        stiffness: 100
      }}
      className="relative group perspective-1000"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ perspective: 1000 }}
    >
      {/* Book number badge */}
      <motion.div
        className="absolute -top-4 -left-4 z-20"
        whileHover={{ scale: 1.2, rotate: 15 }}
      >
        <div className="relative">
          <div className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)',
              boxShadow: '0 4px 20px rgba(245, 158, 11, 0.5)'
            }}
          >
            <span className="font-display text-lg font-bold text-[#0a0a1a]">#{book.id}</span>
          </div>
          {/* Sparkle */}
          <motion.div
            className="absolute -top-1 -right-1"
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
          </motion.div>
        </div>
      </motion.div>

      {/* Main book container */}
      <motion.div
        className="relative rounded-2xl overflow-hidden cursor-pointer"
        style={{
          rotateX: rotateXSpring,
          rotateY: rotateYSpring,
          transformStyle: 'preserve-3d'
        }}
        whileHover={{ z: 50 }}
      >
        {/* Glow effect */}
        <motion.div
          className="absolute -inset-4 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"
          style={{
            background: 'radial-gradient(circle, rgba(107, 76, 154, 0.4) 0%, transparent 70%)',
            filter: 'blur(20px)'
          }}
        />

        {/* Book cover */}
        <div
          className="relative aspect-[3/4] bg-[#1a1a3a] rounded-2xl overflow-hidden"
          style={{
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 40px rgba(107, 76, 154, 0.2)'
          }}
        >
          <img
            src={book.image}
            alt={book.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />

          {/* Shimmer effect on hover */}
          <motion.div
            className="absolute inset-0 opacity-0 group-hover:opacity-100"
            initial={{ x: '-100%' }}
            whileHover={{ x: '100%' }}
            transition={{ duration: 0.6 }}
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)'
            }}
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a1a] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Book spine effect (left edge) */}
          <div
            className="absolute left-0 top-0 bottom-0 w-4"
            style={{
              background: 'linear-gradient(90deg, rgba(0,0,0,0.5), transparent)',
              transform: 'translateZ(10px)'
            }}
          />

          {/* Title overlay on hover */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300"
          >
            <h3 className="font-display text-lg font-bold text-white mb-2 text-glow-gold">
              {book.title}
            </h3>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onReadClick(book)}
              className="flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Read Preview
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const BookShowcase = () => {
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);

  const books = [
    { id: 1, title: 'The First Clue', image: '/images/book1.jpg' },
    { id: 2, title: 'The Case of the Vanishing Cat', image: '/images/book2.jpg' },
    { id: 3, title: 'The Case of the Secret Mirror Passage', image: '/images/book3.jpg' },
    { id: 4, title: 'The Case of the Glittering Jewel Thief', image: '/images/book4.jpg' },
    { id: 5, title: "The Case of the Ancient Mummy's Curse", image: '/images/book5.jpg' },
    { id: 6, title: 'The Case of the Ghost in the Attic', image: '/images/book6.jpg' },
    { id: 7, title: 'The Case of the Poison Plot', image: '/images/book7.jpg' },
    { id: 8, title: 'The Case of the Runaway Robot', image: '/images/book8.jpg' }
  ];

  const handleReadClick = (book) => {
    setSelectedBook(book);
    setIsReaderOpen(true);
  };

  return (
    <div className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        {/* Gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3a 50%, #0f0f2a 100%)'
          }}
        />

        {/* Radial glows */}
        <div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(107, 76, 154, 0.2) 0%, transparent 60%)',
            filter: 'blur(80px)'
          }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(20, 184, 166, 0.15) 0%, transparent 60%)',
            filter: 'blur(80px)'
          }}
        />

        {/* Floating elements */}
        <FloatingElements />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <motion.div
            className="flex items-center justify-center gap-4 mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-purple-500" />
            <BookOpen className="w-8 h-8 text-purple-400" />
            <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-purple-500" />
          </motion.div>

          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="gradient-text-gold">Join The Adventure</span>
          </h2>

          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-serif italic">
            Explore the complete collection of Ned and Nancy's thrilling mysteries.
            Each book is a gateway to a world of wonder and excitement.
          </p>
        </motion.div>

        {/* Books grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-16">
          {books.map((book, index) => (
            <BookCard
              key={book.id}
              book={book}
              index={index}
              onReadClick={handleReadClick}
            />
          ))}
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <motion.button
            onClick={() => {
              setSelectedBook(books[0]);
              setIsReaderOpen(true);
            }}
            whileHover={{
              scale: 1.05,
              boxShadow: '0 20px 60px rgba(245, 158, 11, 0.4)'
            }}
            whileTap={{ scale: 0.95 }}
            className="relative inline-flex items-center gap-3 px-10 py-5 rounded-full font-bold text-xl overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)'
            }}
          >
            {/* Shimmer effect */}
            <motion.div
              className="absolute inset-0"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6 }}
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
              }}
            />
            <BookOpen className="w-6 h-6 text-[#0a0a1a] relative z-10" />
            <span className="text-[#0a0a1a] relative z-10">Start Reading Now!</span>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            >
              <Sparkles className="w-5 h-5 text-[#0a0a1a] relative z-10" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full"
        >
          <path
            d="M0 80L60 74.7C120 69 240 59 360 53.3C480 48 600 48 720 53.3C840 59 960 69 1080 69.3C1200 69 1320 59 1380 53.3L1440 48V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
            fill="#0a0a1a"
          />
        </svg>
      </div>

      {/* Book Reader Panel */}
      <BookPanel
        isOpen={isReaderOpen}
        onClose={() => setIsReaderOpen(false)}
        title={selectedBook?.title || 'The First Clue'}
      >
        <SampleContent book={selectedBook || books[0]} />
      </BookPanel>
    </div>
  );
};

export default BookShowcase;
